/**
 * Shared tag types for API contracts between frontend and backend
 */
import { z } from "zod";
import { nameSchema, descriptionSchema, uuidSchema, hexColorOptionalSchema, nonNegativeIntOptionalSchema, IdParamSchema, CompanyIdParamSchema, } from "./validation-common.js";
/**
 * Resource types that can be tagged
 */
export const TaggableResourceTypeSchema = z.enum([
    "LOCATION",
    "EMPLOYEE",
    "SHIFT",
    "JOB_ROLE",
    "JOB_ROLE_FAMILY",
    "JOB_LEVEL",
    "LOCATION_DOCUMENT",
    "DOCUMENT",
]);
// ===== TAG GROUP TYPES =====
// Schemas for tag group inputs
export const CreateTagGroupInputSchema = z.object({
    name: nameSchema,
    description: descriptionSchema,
});
// For route-level validation (includes companyId)
export const CreateTagGroupBodySchema = z.object({
    name: nameSchema,
    description: descriptionSchema,
    companyId: uuidSchema,
});
export const UpdateTagGroupInputSchema = z.object({
    name: nameSchema.optional(),
    description: descriptionSchema,
});
// API Response schema for TagGroup
export const TagGroupResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    companyId: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ===== TAG TYPES =====
// Schemas for tag inputs
export const CreateTagInputSchema = z.object({
    name: nameSchema,
    color: hexColorOptionalSchema,
    sortOrder: nonNegativeIntOptionalSchema,
});
export const UpdateTagInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
    sortOrder: nonNegativeIntOptionalSchema,
});
// API Response schema for Tag
export const TagResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    tagGroupId: z.uuid(),
    color: z.string().nullable(),
    sortOrder: z.number().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// Tag with parent group information
export const TagWithGroupResponseSchema = TagResponseSchema.extend({
    tagGroup: TagGroupResponseSchema,
});
// TagGroup with nested tags
export const TagGroupWithTagsResponseSchema = TagGroupResponseSchema.extend({
    tags: z.array(TagResponseSchema),
});
// ===== RESOURCE TAG TYPES =====
// Schema for resource tag input
export const CreateResourceTagInputSchema = z.object({
    tagId: uuidSchema,
    resourceType: TaggableResourceTypeSchema,
    resourceId: uuidSchema,
});
// ===== PARAMETER SCHEMAS =====
// Re-export common parameter schemas from validation-common for backwards compatibility
export { IdParamSchema, CompanyIdParamSchema };
// Domain-specific parameter schemas
export const TagGroupIdParamSchema = z.object({
    tagGroupId: uuidSchema,
});
export const TagIdParamSchema = z.object({
    tagId: uuidSchema,
});
export const ResourceParamsSchema = z.object({
    resourceType: TaggableResourceTypeSchema,
    resourceId: uuidSchema,
});
// API Response schema for ResourceTag
export const ResourceTagResponseSchema = z.object({
    id: z.uuid(),
    tagId: z.uuid(),
    resourceType: TaggableResourceTypeSchema,
    resourceId: z.uuid(),
    createdAt: z.iso.datetime(),
});
// ResourceTag with tag and tag group information
export const ResourceTagWithDetailsResponseSchema = ResourceTagResponseSchema.extend({
    tag: TagWithGroupResponseSchema,
});
